import React, {useState, useEffect} from 'react';
import {Layout, Drawer, Button, Modal} from 'antd';
import {
  DoubleRightOutlined,
  LockOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import Sidebar from '../components/Sidebar';
import {Link, Outlet, useLocation, useNavigate} from 'react-router-dom';
import {getRoute} from '../api/routes';
import api from '../Interceptor';
import {ExclamationCircleOutlined} from '@ant-design/icons';
import {useDispatch, useSelector} from 'react-redux';
import {fetchAllExpenses} from '../features/expenses/expenseActions';
import AddProject from '../components/AddProject';
import {setProjectList} from '../slices/app';
import {toast} from 'react-toastify';
import AiAssistent from '../components/AiAssistent';

const {Header, Sider, Content} = Layout;

const Home = ({children}) => {
  const {expenses, expensesLoading} = useSelector((state) => state.expense);
  const {projectList, projectsLoading} = useSelector((state) => state.app);
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const location = useLocation();
  const loading = expensesLoading || projectsLoading;
  const [collapsed] = useState(false);
  const [activePage, setActivePage] = useState('dashboard');
  const [isMobile, setIsMobile] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const navigate = useNavigate();
  // In Home.jsx
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);

  useEffect(() => {
    const handler = () => {
      setShowAddProjectModal(true); // will only set true, not toggle
    };
    window.addEventListener('openAddProjectModal', handler);
    return () => window.removeEventListener('openAddProjectModal', handler);
  }, []);

  const colorBgContainer = '#EDF1F1';
  const isUnpaid =
    user?.paymentStatus === process.env.REACT_APP_UNPAID_SUBSCRIPTION_STATUS_ID;
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (user?.userRole?.name) {
      dispatch(
        fetchAllExpenses({
          userRole: user.userRole.name,
          currencyView: 'BaseCurrency',
        })
      );
    }
  }, [dispatch, user]);

  // Fetch notifications on component mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await api.get(
          `${getRoute('users', 'admin', 'notification', false, false, '')}`
        );

        const data = response.data;

        if (data.length > 0) {
          showNotificationsSequentially(data, 0);
        }
      } catch (error) {
        if (error.isUnpaid) {
          return;
        }
        console.error('Error fetching notifications:', error);
      }
    };

    fetchNotifications();
  }, []);

  const showNotificationsSequentially = (notifications, index) => {
    if (index >= notifications.length) return;

    const notificationData = notifications[index];

    Modal.confirm({
      title: (
        <span className="text-xl font-bold text-themeColor">
          {notificationData.title || 'New Notification'}
        </span>
      ),
      icon: <ExclamationCircleOutlined className="text-themeColor text-3xl" />,
      content: (
        <p className="text-lg text-gray-700 mt-2">
          {notificationData.description || 'You have a new update.'}
        </p>
      ),
      centered: true,
      okText: 'Ok',
      okButtonProps: {
        className: 'bg-themeGradient rounded-lg text-lg px-5 py-2 h-10',
      },
      cancelButtonProps: {className: 'hidden'},
      maskClosable: false,
      onOk: () => showNotificationsSequentially(notifications, index + 1),
    });
  };

  const toggleDrawer = () => setDrawerVisible(!drawerVisible);
  const fetchProjects = async () => {
    try {
      const response = await api.get(
        getRoute('users', 'admin', 'projects', false, false, '')
      );
      dispatch(setProjectList(response.data));
    } catch (error) {
      if (error?.isUnpaid) {
        return;
      }
      if (error.response && error.response.data) {
        toast.error(`Failed to fetch projects: ${error.response.data.message}`);
      } else {
        toast.error('An error occurred while fetching projects.');
      }
      console.error('Fetch projects error:', error);
    }
  };
  return (
    <>
      {showAddProjectModal && (
        <AddProject
          isOpen={showAddProjectModal}
          toggleModal={() => setShowAddProjectModal(false)}
          fetchProjects={fetchProjects}
        />
      )}

      <Layout className="min-h-[100vh] hidden-overflow">
        {!isMobile && (
          <Sider
            trigger={null}
            collapsible
            collapsed={collapsed}
            style={{
              background: '#EDF1F1',
              position: 'fixed',
              left: 0,
              zIndex: 111,
            }}
          >
            <Sidebar setActivePage={setActivePage} activePage={activePage} />
          </Sider>
        )}

        {isMobile && (
          <Drawer
            title="Menu"
            placement="left"
            onClose={() => setDrawerVisible(false)}
            open={drawerVisible}
            bodyStyle={{padding: 0}}
            zIndex={1500}
            style={{width: '250px', boxShadow: 'none'}}
            contentWrapperStyle={{boxShadow: 'none'}}
          >
            <Sidebar
              setActivePage={setActivePage}
              activePage={activePage}
              onMenuSelect={() => setDrawerVisible(false)}
            />
          </Drawer>
        )}

        <Layout
          className={`bg-[#EDF1F1] ${
            isMobile ? 'ml-0' : collapsed ? 'ml-20' : 'ml-[250px]'
          }`}
        >
          {isMobile && (
            <Header className="px-4 py-2 bg-white flex items-center">
              <Button
                icon={<MenuOutlined />}
                onClick={toggleDrawer}
                className="bg-[#fff] border-none text-[16px]"
              />
              <p className="ml-2 text-[16px]">
                Welcome{' '}
                <span className="font-semibold">
                  {localStorage.getItem('name')}
                </span>
              </p>
            </Header>
          )}
          <Content
            className={`min-h-screen bg-[#eef3f3] z-1 ${
              isUnpaid && location.pathname !== '/packages'
                ? 'blur-sm pointer-events-none select-none'
                : ''
            }`}
          >
            <div className="w-full mx-auto">
              <div className="w-full mx-auto">
                {(!projectList || projectList.length === 0) && (
                  <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 m-3 mb-0 rounded-2xl shadow-sm">
                    <div className="flex items-center justify-center min-w-6 h-6 rounded-full bg-blue-100 text-blue-600 font-bold">
                      i
                    </div>
                    <p className="text-sm md:text-base">
                      Please{' '}
                      <span
                        className="font-semibold underline cursor-pointer"
                        role="button"
                        tabIndex={0}
                        onClick={() => setShowAddProjectModal(true)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') setShowAddProjectModal(true);
                        }}
                      >
                        Create a Project
                      </span>{' '}
                      to start tracking transactions.
                    </p>
                  </div>
                )}
                {!loading &&
                  projectList.length > 0 &&
                  expenses.length === 0 && (
                    <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 m-3 mb-0 rounded-2xl shadow-sm">
                      <div className="flex items-center justify-center min-w-6 h-6 rounded-full bg-blue-100 text-blue-600 font-bold">
                        i
                      </div>
                      <p className="text-sm md:text-base">
                        Start adding your{' '}
                        <span
                          className="font-semibold underline cursor-pointer"
                          role="button"
                          tabIndex={0}
                          onClick={() =>
                            navigate('/expenseList', {
                              state: {openAddExpense: true},
                            })
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter')
                              navigate('/expenseList', {
                                state: {openAddExpense: true},
                              });
                          }}
                        >
                          Transactions
                        </span>{' '}
                        now to effortlessly track and manage your expenses.
                      </p>
                    </div>
                  )}
              </div>
            </div>
            {children || <Outlet />}
            {location.pathname !== '/chats' && <AiAssistent />}
          </Content>
          {isUnpaid && location.pathname !== '/packages' && (
            <div className="absolute inset-0 flex flex-col justify-center items-center z-50 ">
              <div className="bg-red-100 p-6 rounded-xl shadow-lg text-center border border-red-400 text-red-700 ms-[200px]">
                <LockOutlined className="text-5xl text-red-500 mb-3" />
                <h2 className="text-xl font-semibold mb-2">
                  Subscription Required
                </h2>
                <p className="text-gray-600 mb-4">
                  Your account doesn’t have an active subscription. To unlock
                  all features and continue using our services, please select a
                  plan that suits you.
                </p>
                <Link
                  to="/packages"
                  className="bg-themeGradient text-white px-6 py-2 rounded-lg text-lg"
                >
                  Explore Plans
                  <span className="animate-move-right ml-2 inline-block ">
                    <DoubleRightOutlined />
                  </span>
                </Link>
              </div>
            </div>
          )}
        </Layout>
      </Layout>
    </>
  );
};

export default Home;
